import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Loader2, CheckCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AuthCallback - Handles OAuth redirect from Emergent Auth
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
const AuthCallback = () => {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processCallback = async () => {
      try {
        // Extract session_id from URL fragment
        const hash = window.location.hash;
        const sessionIdMatch = hash.match(/session_id=([^&]+)/);
        
        if (!sessionIdMatch) {
          console.error('No session_id found in callback URL');
          navigate('/', { replace: true });
          return;
        }

        const sessionId = sessionIdMatch[1];

        // Exchange session_id for user data via backend
        const response = await fetch(`${API}/auth/session?session_id=${sessionId}`, {
          method: 'GET',
          credentials: 'include', // Important for cookies
        });

        if (!response.ok) {
          throw new Error('Failed to exchange session');
        }

        const userData = await response.json();

        // Store user in context and localStorage
        const user = {
          id: userData.user_id,
          email: userData.email,
          name: userData.name,
          picture: userData.picture,
          subscription: userData.subscription || 'free',
          createdAt: userData.created_at
        };

        setUser(user);
        localStorage.setItem('legalconverter_user', JSON.stringify(user));

        // Navigate to dashboard
        navigate('/dashboard', { replace: true, state: { user } });
      } catch (error) {
        console.error('Auth callback error:', error);
        navigate('/', { replace: true });
      }
    };

    processCallback();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full mx-4 text-center">
        <div className="animate-pulse">
          <Loader2 className="h-16 w-16 text-blue-600 mx-auto animate-spin mb-4" />
        </div>
        <h2 className="text-2xl font-bold text-slate-900 mb-2">Signing you in...</h2>
        <p className="text-slate-600">Please wait while we complete your authentication.</p>
      </div>
    </div>
  );
};

export default AuthCallback;
